import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * MoneySend component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconMoneySend extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon cat-c-icon-bi-directional', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_10751_4800)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0 5.99997C0 5.52658 0.383756 5.14282 0.857143 5.14282H7.71429C8.18767 5.14282 8.57143 5.52658 8.57143 5.99997C8.57143 6.47335 8.18767 6.85711 7.71429 6.85711H0.857143C0.383756 6.85711 0 6.47335 0 5.99997Z"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0 11.1428C0 10.6694 0.383756 10.2856 0.857143 10.2856H6C6.47339 10.2856 6.85714 10.6694 6.85714 11.1428C6.85714 11.6162 6.47339 11.9999 6 11.9999H0.857143C0.383756 11.9999 0 11.6162 0 11.1428Z"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0 16.2857C0 15.8123 0.383756 15.4286 0.857143 15.4286H4.28571C4.7591 15.4286 5.14286 15.8123 5.14286 16.2857C5.14286 16.7591 4.7591 17.1429 4.28571 17.1429H0.857143C0.383756 17.1429 0 16.7591 0 16.2857Z"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M11.1424 5.99997C11.1424 5.52658 11.5262 5.14282 11.9996 5.14282H21.4282C22.1101 5.14282 22.7642 5.41374 23.2464 5.89598C23.7287 6.37821 23.9996 7.03227 23.9996 7.71425V16.2857C23.9996 16.9677 23.7287 17.6217 23.2464 18.104C22.7642 18.5862 22.1101 18.8571 21.4282 18.8571H8.57101C8.09762 18.8571 7.71387 18.4734 7.71387 18C7.71387 17.5266 8.09762 17.1428 8.57101 17.1428H21.4282C21.6555 17.1428 21.8735 17.0525 22.0342 16.8918C22.195 16.731 22.2853 16.513 22.2853 16.2857V7.71425C22.2853 7.48692 22.195 7.26891 22.0342 7.10816C21.8735 6.94741 21.6555 6.85711 21.4282 6.85711H11.9996C11.5262 6.85711 11.1424 6.47335 11.1424 5.99997Z"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M13.7147 10.2857C12.7679 10.2857 12.0004 11.0532 12.0004 12C12.0004 12.9468 12.7679 13.7143 13.7147 13.7143C14.6615 13.7143 15.429 12.9468 15.429 12C15.429 11.0532 14.6615 10.2857 13.7147 10.2857ZM10.2861 12C10.2861 10.1064 11.8212 8.57141 13.7147 8.57141C15.6083 8.57141 17.1433 10.1064 17.1433 12C17.1433 13.8935 15.6083 15.4286 13.7147 15.4286C11.8212 15.4286 10.2861 13.8935 10.2861 12Z"
            />
          </g>
          <defs>
            <clipPath id="clip0_10751_4800">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-money-send') === undefined) {
  customElements.define('cat-icon-money-send', BlocksIconMoneySend);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-money-send': BlocksIconMoneySend;
  }
}
